import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { blogArticles } from '../mock/mockData';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import InternalLinks from '../components/seo/InternalLinks';

const ArticleCard = ({ article, featured = false }) => (
  <Link to={`/blog/${article.slug}`} data-testid={`blog-card-${article.slug}`}>
    <Card className="border-2 border-gray-200 hover:border-alden-green hover:shadow-xl transition-all h-full group overflow-hidden">
      <div className={`overflow-hidden ${featured ? 'h-56' : 'h-44'}`}>
        <img
          src={article.image}
          alt={`${article.title} - Alden Technologies guide`}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <CardContent className="p-6">
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
          <span className="bg-alden-green/10 text-alden-green px-3 py-1 rounded-full font-semibold">{article.category}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{article.readTime}</span>
        </div>
        <h3 className={`font-bold text-gray-900 mb-2 group-hover:text-alden-green transition-colors ${featured ? 'text-xl' : 'text-lg'}`}>
          {article.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-3">{article.excerpt}</p>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-alden-green">
          Read Article <ArrowRight className="w-4 h-4" />
        </span>
      </CardContent>
    </Card>
  </Link>
);

export default function Blog() {
  const featured = blogArticles.filter((a) => a.featured);
  const rest = blogArticles.filter((a) => !a.featured);

  return (
    <div className="min-h-screen" data-testid="blog-index-page">
      <SEOHead
        title="Knowledge Hub — Mineral Water Plant Guides & Articles | Alden Technologies"
        description="Expert guides on mineral water plant cost, BIS certification, FSSAI licensing, machinery buying guides, profitability and turnkey plant setup — written by Alden Technologies engineers."
        path="/blog"
      />
      <Breadcrumbs items={[{ label: 'Knowledge Hub' }]} />

      <section className="py-16 md:py-20 bg-gradient-to-br from-alden-green to-alden-green-light text-white">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Knowledge Hub</h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl">
            In-depth guides on plant cost, licensing, machinery selection and profitability — everything you need to plan your mineral water business, written by our engineering team.
          </p>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Featured Guides</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {featured.map((a) => (
              <ArticleCard key={a.slug} article={a} featured />
            ))}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">All Articles</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-gradient-to-br from-alden-green to-alden-green-light text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-4">Planning Your Water Plant?</h2>
          <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto">
            Skip the guesswork — get a free capacity-wise project report and quotation from our engineers.
          </p>
          <Link to="/contact" data-testid="blog-index-cta">
            <span className="inline-flex items-center gap-2 bg-white text-alden-green font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors text-lg">
              Request Project Quotation <ArrowRight className="w-5 h-5" />
            </span>
          </Link>
        </div>
      </section>

      <InternalLinks currentPath="/blog" />
    </div>
  );
}
